import React, { useEffect, useState } from 'react';
import {
  Box, Paper, Typography, Autocomplete, TextField, Button, CircularProgress,
  Card, CardContent, Chip, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Dialog, DialogTitle, DialogContent, DialogActions, Alert, Tabs, Tab,
  LinearProgress, Divider, Accordion, AccordionSummary, AccordionDetails,
} from '@mui/material';
import {
  Psychology as BrainIcon, PlayArrow as RunIcon, Build as FixIcon,
  ExpandMore as ExpandIcon, History as HistoryIcon, CheckCircle as OkIcon,
  Cancel as FailIcon, Science as HypothesisIcon,
} from '@mui/icons-material';
import { useLanguage } from '../contexts/LanguageContext';
import { itemsService } from '../services/items.service';
import { Item } from '../types/items.types';
import {
  itemAuditService, ItemAuditResult, FixPlan, FixExecutionResult, FixHistoryEntry,
} from '../services/itemAudit.service';
import { toast } from 'react-toastify';

// عارض بسيط للتقرير السردي (عناوين ماركداون + نقاط + غامق)
const NarrativeView: React.FC<{ text: string; isRTL: boolean }> = ({ text, isRTL }) => (
  <Box dir={isRTL ? 'rtl' : 'ltr'} sx={{ '& p': { my: 0.5 } }}>
    {text.split('\n').map((line, i) => {
      const bold = (s: string) =>
        s.split('**').map((part, j) => (j % 2 === 1 ? <strong key={j}>{part}</strong> : part));
      if (line.startsWith('# ')) return <Typography key={i} variant="h5" sx={{ mt: 1, mb: 1, fontWeight: 700 }}>{line.slice(2)}</Typography>;
      if (line.startsWith('## ')) return <Typography key={i} variant="h6" sx={{ mt: 2, mb: 0.5, fontWeight: 700 }}>{line.slice(3)}</Typography>;
      if (line.startsWith('### ')) return <Typography key={i} variant="subtitle1" sx={{ mt: 1.5, fontWeight: 700 }}>{line.slice(4)}</Typography>;
      if (line.startsWith('- ')) return <Typography key={i} component="li" variant="body2" sx={{ mr: isRTL ? 2 : 0, ml: isRTL ? 0 : 2 }}>{bold(line.slice(2))}</Typography>;
      if (line.trim() === '---') return <Divider key={i} sx={{ my: 1 }} />;
      if (line.trim() === '') return <Box key={i} sx={{ height: 6 }} />;
      return <Typography key={i} variant="body2" component="p">{bold(line)}</Typography>;
    })}
  </Box>
);

// صيغة التاريخ المعتمدة في النظام: dd-mm-yyyy
const fmtDate = (d: string | Date): string => {
  const dt = new Date(d);
  const dd = String(dt.getDate()).padStart(2, '0');
  const mm = String(dt.getMonth() + 1).padStart(2, '0');
  return `${dd}-${mm}-${dt.getFullYear()}`;
};
const fmtDateTime = (d: string | Date): string => {
  const dt = new Date(d);
  const hh = String(dt.getHours()).padStart(2, '0');
  const mi = String(dt.getMinutes()).padStart(2, '0');
  return `${fmtDate(dt)} ${hh}:${mi}`;
};

const ItemAuditPage: React.FC = () => {
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const lang = isRTL ? 'ar' : 'en';

  const t = {
    title: isRTL ? '🧠 المحلل الخبير للأصناف' : '🧠 Smart Item Auditor',
    subtitle: isRTL
      ? 'فحص كامل لدورة حياة الصنف: يكتشف الخلل، يحدد ميلاده (التاريخ/السند/المستخدم)، يشرح باحترافية، ويصلح بموافقتك مع سجل تتبع دائم'
      : 'Full item lifecycle audit: detects defects, pinpoints their birth (date/document/user), explains professionally, and fixes upon your approval with a permanent trail',
    selectItem: isRTL ? 'اختر الصنف' : 'Select item',
    runAudit: isRTL ? 'فحص شامل' : 'Run Full Audit',
    auditTab: isRTL ? 'الفحص' : 'Audit',
    historyTab: isRTL ? 'سجل الإصلاحات' : 'Fix History',
    healthScore: isRTL ? 'درجة الصحة' : 'Health Score',
    findings: isRTL ? 'النتائج المكتشفة' : 'Findings',
    noFindings: isRTL ? '✅ الصنف سليم تماماً — لا يوجد أي خلل' : '✅ Item is completely healthy — no defects found',
    report: isRTL ? 'التقرير الاحترافي' : 'Professional Report',
    timeline: isRTL ? 'الخط الزمني الكامل' : 'Full Timeline',
    previewFix: isRTL ? 'عرض خطة الإصلاح' : 'Preview Fix Plan',
    confirmExecute: isRTL ? '✅ أؤكد — نفّذ الإصلاح الآن' : '✅ Confirm — Execute Fix Now',
    cancel: isRTL ? 'إلغاء' : 'Cancel',
    close: isRTL ? 'إغلاق' : 'Close',
    executing: isRTL ? 'جارٍ التنفيذ...' : 'Executing...',
    fixPlanTitle: isRTL ? 'خطة الإصلاح — للمراجعة قبل التنفيذ' : 'Fix Plan — Review Before Execution',
    changesTable: isRTL ? 'جدول التغييرات (قبل ← بعد)' : 'Changes Table (before → after)',
    colDoc: isRTL ? 'المستند/الموضع' : 'Document/Location',
    colDesc: isRTL ? 'الوصف' : 'Description',
    colOld: isRTL ? 'قبل' : 'Before',
    colNew: isRTL ? 'بعد' : 'After',
    execResult: isRTL ? 'نتيجة التنفيذ' : 'Execution Result',
    steps: isRTL ? 'الخطوات المنفذة (مستنداً بمستند)' : 'Executed steps (document by document)',
    date: isRTL ? 'التاريخ' : 'Date',
    docNo: isRTL ? 'المستند' : 'Document',
    desc: isRTL ? 'الوصف' : 'Description',
    qty: isRTL ? 'الكمية' : 'Qty',
    unit: isRTL ? 'الوحدة' : 'Unit',
    cost: isRTL ? 'التكلفة' : 'Cost',
    location: isRTL ? 'الموقع' : 'Location',
    user: isRTL ? 'المستخدم' : 'User',
    birth: isRTL ? 'ميلاد الخلل' : 'Defect birth',
    confidence: isRTL ? 'الثقة' : 'Confidence',
    autoFixable: isRTL ? 'قابل للإصلاح الآلي' : 'Auto-fixable',
    manualOnly: isRTL ? 'يتطلب تحليلاً يدوياً' : 'Manual analysis required',
    executedBy: isRTL ? 'نفّذه' : 'By',
    changes: isRTL ? 'تغييراً' : 'changes',
    loadingAudit: isRTL ? 'المحلل الخبير يفحص دورة حياة الصنف بالكامل...' : 'Expert analyzer examining the full item lifecycle...',
    stats: isRTL ? 'إحصائيات' : 'Stats',
  };

  const [items, setItems] = useState<Item[]>([]);
  const [selectedItem, setSelectedItem] = useState<Item | null>(null);
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ItemAuditResult | null>(null);
  const [fixPlan, setFixPlan] = useState<FixPlan | null>(null);
  const [fixDialogOpen, setFixDialogOpen] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [execResult, setExecResult] = useState<FixExecutionResult | null>(null);
  const [history, setHistory] = useState<FixHistoryEntry[]>([]);

  useEffect(() => {
    itemsService.getAll(true).then(setItems).catch(() => toast.error(isRTL ? 'فشل تحميل الأصناف' : 'Failed to load items'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runAudit = async () => {
    if (!selectedItem) return;
    setLoading(true);
    setResult(null);
    setExecResult(null);
    try {
      const r = await itemAuditService.audit(selectedItem.itemId, lang);
      setResult(r);
    } catch {
      toast.error(isRTL ? 'فشل الفحص' : 'Audit failed');
    } finally {
      setLoading(false);
    }
  };

  const openFixPlan = async (findingCode: string) => {
    if (!selectedItem) return;
    try {
      const plan = await itemAuditService.getFixPlan(selectedItem.itemId, findingCode, lang);
      setFixPlan(plan);
      setFixDialogOpen(true);
    } catch {
      toast.error(isRTL ? 'فشل توليد خطة الإصلاح' : 'Failed to build fix plan');
    }
  };

  const executeFix = async () => {
    if (!selectedItem || !fixPlan) return;
    setExecuting(true);
    try {
      const r = await itemAuditService.executeFix(selectedItem.itemId, fixPlan.findingCode, lang);
      setExecResult(r);
      setFixDialogOpen(false);
      if (r.success) {
        toast.success(r.message);
        await runAudit(); // إعادة الفحص وعرض النتيجة النظيفة
      } else {
        toast.error(r.message);
      }
    } catch {
      toast.error(isRTL ? 'فشل التنفيذ' : 'Execution failed');
    } finally {
      setExecuting(false);
    }
  };

  const loadHistory = async () => {
    try {
      setHistory(await itemAuditService.getHistory());
    } catch {
      toast.error(isRTL ? 'فشل تحميل السجل' : 'Failed to load history');
    }
  };

  useEffect(() => {
    if (tab === 1) loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const severityColor = (s: string): 'error' | 'warning' | 'info' =>
    s === 'Critical' ? 'error' : s === 'Warning' ? 'warning' : 'info';

  const healthColor = (score: number) => (score >= 90 ? 'success.main' : score >= 60 ? 'warning.main' : 'error.main');

  return (
    <Box sx={{ p: 3 }} dir={isRTL ? 'rtl' : 'ltr'}>
      <Paper sx={{ p: 2, mb: 2, background: 'linear-gradient(135deg, #1a237e 0%, #4a148c 100%)', color: 'white' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <BrainIcon fontSize="large" />
          <Box>
            <Typography variant="h5" fontWeight={700}>{t.title}</Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>{t.subtitle}</Typography>
          </Box>
        </Box>
      </Paper>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab icon={<RunIcon />} iconPosition="start" label={t.auditTab} />
        <Tab icon={<HistoryIcon />} iconPosition="start" label={t.historyTab} />
      </Tabs>

      {tab === 0 && (
        <>
          <Paper sx={{ p: 2, mb: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <Autocomplete
              sx={{ minWidth: 340, flex: 1 }}
              options={items}
              value={selectedItem}
              onChange={(_, v) => setSelectedItem(v)}
              getOptionLabel={(o) => `${o.itemCode} — ${isRTL ? o.itemNameAr || o.itemName : o.itemName}`}
              isOptionEqualToValue={(o, v) => o.itemId === v.itemId}
              renderInput={(params) => <TextField {...params} label={t.selectItem} size="small" />}
            />
            <Button variant="contained" size="large" startIcon={<RunIcon />} disabled={!selectedItem || loading} onClick={runAudit}>
              {t.runAudit}
            </Button>
          </Paper>

          {loading && (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <CircularProgress />
              <Typography sx={{ mt: 2 }}>{t.loadingAudit}</Typography>
            </Paper>
          )}

          {execResult && (
            <Alert severity={execResult.success ? 'success' : 'error'} sx={{ mb: 2 }}>
              <Typography fontWeight={700}>{t.execResult}: {execResult.message}</Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>{execResult.verification}</Typography>
              {execResult.executedSteps.length > 0 && (
                <Accordion sx={{ mt: 1, bgcolor: 'transparent' }} elevation={0}>
                  <AccordionSummary expandIcon={<ExpandIcon />}>
                    <Typography variant="body2" fontWeight={600}>{t.steps} ({execResult.executedSteps.length})</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    {execResult.executedSteps.map((s, i) => (
                      <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 0.5 }}>
                        {s.success ? <OkIcon color="success" fontSize="small" /> : <FailIcon color="error" fontSize="small" />}
                        <Typography variant="body2"><strong>{s.documentRef}:</strong> {s.description}</Typography>
                      </Box>
                    ))}
                  </AccordionDetails>
                </Accordion>
              )}
            </Alert>
          )}

          {result && !loading && (
            <>
              {/* درجة الصحة + إحصائيات */}
              <Paper sx={{ p: 2, mb: 2 }}>
                <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Box sx={{ minWidth: 180 }}>
                    <Typography variant="subtitle2">{t.healthScore}</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="h3" fontWeight={800} sx={{ color: healthColor(result.healthScore) }}>
                        {result.healthScore}
                      </Typography>
                      <Typography variant="h6" color="text.secondary">/100</Typography>
                    </Box>
                    <LinearProgress variant="determinate" value={result.healthScore}
                      sx={{ height: 8, borderRadius: 4, '& .MuiLinearProgress-bar': { bgcolor: healthColor(result.healthScore) } }} />
                  </Box>
                  <Divider orientation="vertical" flexItem />
                  <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                    <Chip label={`${isRTL ? 'حركات' : 'Movements'}: ${result.stats.totalMovements}`} />
                    <Chip label={`${isRTL ? 'مستندات' : 'Documents'}: ${result.stats.totalDocuments}`} />
                    <Chip label={`${isRTL ? 'قيود مرتبطة' : 'Related JEs'}: ${result.stats.relatedJournalEntries}`} />
                    <Chip label={`${isRTL ? 'مواقع' : 'Locations'}: ${result.stats.locationsCount}`} />
                    <Chip color="primary" label={`${isRTL ? 'القيمة الحالية' : 'Current value'}: ${result.stats.currentTotalValue.toFixed(3)}`} />
                  </Box>
                </Box>
              </Paper>

              {/* النتائج */}
              <Typography variant="h6" sx={{ mb: 1 }}>{t.findings}</Typography>
              {result.findings.length === 0 ? (
                <Alert severity="success" sx={{ mb: 2 }}>{t.noFindings}</Alert>
              ) : (
                result.findings.map((f, i) => (
                  <Card key={i} sx={{ mb: 2, borderInlineStart: 6, borderInlineStartStyle: 'solid', borderInlineStartColor: `${severityColor(f.severity)}.main` }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1 }}>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          <Chip size="small" color={severityColor(f.severity)} label={f.severity} />
                          <Typography variant="h6">{f.title}</Typography>
                        </Box>
                        {f.canAutoFix ? (
                          <Button variant="contained" color="success" startIcon={<FixIcon />} onClick={() => openFixPlan(f.findingCode)}>
                            {t.previewFix}
                          </Button>
                        ) : (
                          <Chip size="small" variant="outlined" label={t.manualOnly} />
                        )}
                      </Box>
                      <Typography variant="body1" sx={{ mt: 1 }}>{f.explanation}</Typography>
                      {f.evidence.length > 0 && (
                        <Box component="ul" sx={{ mt: 1, mb: 0 }}>
                          {f.evidence.map((e, j) => <Typography key={j} component="li" variant="body2">{e}</Typography>)}
                        </Box>
                      )}
                      {f.birthDate && (
                        <Alert severity="info" sx={{ mt: 1 }} icon={false}>
                          <strong>{t.birth}:</strong> {fmtDate(f.birthDate)} — {f.birthDocument}
                          {f.birthUser ? ` — ${t.user}: ${f.birthUser}` : ''}
                        </Alert>
                      )}
                      {f.hypothesis && (
                        <Alert severity="warning" sx={{ mt: 1 }} icon={<HypothesisIcon />}>
                          <strong>({t.confidence} {f.confidencePercent}%)</strong> {f.hypothesis}
                          {f.confirmationQuestion && <Typography variant="body2" sx={{ mt: 0.5 }}>{f.confirmationQuestion}</Typography>}
                        </Alert>
                      )}
                    </CardContent>
                  </Card>
                ))
              )}

              {/* التقرير السردي */}
              <Accordion defaultExpanded sx={{ mb: 2 }}>
                <AccordionSummary expandIcon={<ExpandIcon />}>
                  <Typography variant="h6">📋 {t.report}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <NarrativeView text={result.narrativeReport} isRTL={isRTL} />
                </AccordionDetails>
              </Accordion>

              {/* الخط الزمني */}
              <Accordion sx={{ mb: 2 }}>
                <AccordionSummary expandIcon={<ExpandIcon />}>
                  <Typography variant="h6">🕐 {t.timeline} ({result.timeline.length})</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <TableContainer sx={{ maxHeight: 440 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>{t.date}</TableCell>
                          <TableCell>{t.docNo}</TableCell>
                          <TableCell>{t.desc}</TableCell>
                          <TableCell align="center">{t.qty}</TableCell>
                          <TableCell>{t.unit}</TableCell>
                          <TableCell align="center">{t.cost}</TableCell>
                          <TableCell>{t.location}</TableCell>
                          <TableCell>{t.user}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {result.timeline.map((e, i) => (
                          <TableRow key={i} sx={e.isDefective ? { bgcolor: 'error.light', '& td': { fontWeight: 600 } } : {}}>
                            <TableCell>{fmtDate(e.date)}</TableCell>
                            <TableCell>{e.documentNumber}</TableCell>
                            <TableCell>{e.description}</TableCell>
                            <TableCell align="center">{e.quantity.toFixed(3)}</TableCell>
                            <TableCell>{e.unitName}</TableCell>
                            <TableCell align="center">{e.unitCost.toFixed(4)}</TableCell>
                            <TableCell>{e.location}</TableCell>
                            <TableCell>{e.createdBy}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              </Accordion>
            </>
          )}
        </>
      )}

      {tab === 1 && (
        <Paper sx={{ p: 2 }}>
          {history.length === 0 ? (
            <Typography color="text.secondary">{isRTL ? 'لا توجد إصلاحات منفذة بعد.' : 'No fixes executed yet.'}</Typography>
          ) : (
            history.map((h) => (
              <Accordion key={h.fixOperationId}>
                <AccordionSummary expandIcon={<ExpandIcon />}>
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Chip size="small" label={`#${h.fixOperationId}`} />
                    <Typography fontWeight={600}>{h.itemCode} — {h.itemName}</Typography>
                    <Chip size="small" color="primary" label={h.findingCode} />
                    <Typography variant="body2" color="text.secondary">
                      {fmtDateTime(h.executedDate)} — {t.executedBy}: {h.executedBy} — {h.changesCount} {t.changes}
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2" sx={{ mb: 1 }}>{h.summary}</Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>{t.colDoc}</TableCell>
                          <TableCell>{t.colDesc}</TableCell>
                          <TableCell align="center">{t.colOld}</TableCell>
                          <TableCell align="center">{t.colNew}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {h.changes.map((c, i) => (
                          <TableRow key={i}>
                            <TableCell>{c.documentRef}</TableCell>
                            <TableCell>{c.description}</TableCell>
                            <TableCell align="center">{c.oldValue}</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 700 }}>{c.newValue}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              </Accordion>
            ))
          )}
        </Paper>
      )}

      {/* حوار خطة الإصلاح */}
      <Dialog open={fixDialogOpen} onClose={() => !executing && setFixDialogOpen(false)} maxWidth="lg" fullWidth dir={isRTL ? 'rtl' : 'ltr'}>
        <DialogTitle sx={{ bgcolor: 'warning.light' }}>🛠️ {t.fixPlanTitle}</DialogTitle>
        <DialogContent dividers>
          {fixPlan && (
            <>
              {fixPlan.confirmationQuestion && (
                <Alert severity="warning" sx={{ mb: 2 }}>{fixPlan.confirmationQuestion}</Alert>
              )}
              <NarrativeView text={fixPlan.report} isRTL={isRTL} />
              {fixPlan.changes.length > 0 && (
                <>
                  <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>{t.changesTable} ({fixPlan.changes.length})</Typography>
                  <TableContainer sx={{ maxHeight: 340 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>{t.colDoc}</TableCell>
                          <TableCell>{t.colDesc}</TableCell>
                          <TableCell align="center">{t.colOld}</TableCell>
                          <TableCell align="center">{t.colNew}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {fixPlan.changes.map((c, i) => (
                          <TableRow key={i}>
                            <TableCell>{c.documentRef}</TableCell>
                            <TableCell>{c.description}</TableCell>
                            <TableCell align="center">{c.oldValue}</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 700, color: 'success.main' }}>{c.newValue}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFixDialogOpen(false)} disabled={executing}>{t.cancel}</Button>
          <Button
            variant="contained" color="success" startIcon={executing ? <CircularProgress size={18} /> : <FixIcon />}
            disabled={executing || !fixPlan || fixPlan.changes.length === 0}
            onClick={executeFix}
          >
            {executing ? t.executing : t.confirmExecute}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ItemAuditPage;
